import { useMemo } from 'react'
import type { Product } from '@/types'
import type { ProductSizePick } from '@/lib/productSize'
import { homeSectionSizePick } from '@/lib/productSize'
import { ProductCard } from '@/store/components/ProductCard'
import { ProductGridTitleProvider } from '@/store/components/ProductGridTitleSync'
import { cn } from '@/lib/utils'

interface HomeProductGridProps {
  products: Product[]
  className?: string
  /** When set, every card uses this strategy instead of home index rules. */
  uniformSizePick?: ProductSizePick
  /** Per-product selected variant (shop page — drives price sort). */
  selectedSizes?: Record<string, string>
  onSizeChange?: (productId: string, size: string) => void
  /** Product to pulse after a variant change re-sorts the grid. */
  sortHighlight?: { productId: string; token: number } | null
}

/**
 * Home page product grids: 1st & 4th cards use first in-stock variant;
 * 2nd & 3rd use a random variant (random even when all are out of stock).
 */
export function HomeProductGrid({
  products,
  className,
  uniformSizePick,
  selectedSizes,
  onSizeChange,
  sortHighlight,
}: HomeProductGridProps) {
  const productIds = useMemo(() => products.map((p) => p.id), [products])

  return (
    <ProductGridTitleProvider productIds={productIds}>
      <div
        className={cn(
          'grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6',
          className
        )}
      >
        {products.map((product, index) => {
          const defaultSizePick = uniformSizePick ?? homeSectionSizePick(index)
          return (
            <ProductCard
              key={product.id}
              product={product}
              showActions
              ratingVariant="home-star"
              defaultSizePick={defaultSizePick}
              selectedSize={selectedSizes?.[product.id]}
              onSizeChange={
                onSizeChange
                  ? (size) => onSizeChange(product.id, size)
                  : undefined
              }
              sortHighlighted={sortHighlight?.productId === product.id}
              sortHighlightToken={
                sortHighlight?.productId === product.id
                  ? sortHighlight.token
                  : undefined
              }
              imagePriority={index < 4}
            />
          )
        })}
      </div>
    </ProductGridTitleProvider>
  )
}
